"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDiscountsSummary = exports.getWholesalerDiscountsSummaryWithCurrency = exports.getWholesalerDiscountsSummary = exports.getWholesalerSummary = exports.getProductListDiscountsSummary = exports.getProductDiscountsSummary = void 0;
const _discounts_1 = require(".");
const error_1 = require("@creoate/core-lib/error");
const _shared_1 = require("../shared");
// #region Product
/**
 * Calculate and summarize a single product discounts and totals
 * (subtotal, discounts summary and total).
 * @param product The product to summarize the discounts and totals
 * @returns The discounted product information (after sale price - bulk discount)
 */
const getProductDiscountsSummary = (product, storeDiscountPercentage) => {
    let discountedProducts;
    if (storeDiscountPercentage) {
        // Direct, Bulk and Store discount
        discountedProducts = (0, _discounts_1.getProductStoreDiscount)(product, storeDiscountPercentage);
    }
    else {
        // Direct and Bulk discount
        discountedProducts = (0, _discounts_1.getProductBulkDiscount)(product);
    }
    return discountedProducts;
};
exports.getProductDiscountsSummary = getProductDiscountsSummary;
/**
 * Calculate and summarize a list of product discounts and totals.
 * See the `getProductSummary` method description for more details.
 * @param products List of products to calculate and summarize
 * @returns A summary object of all products provided
 */
const getProductListDiscountsSummary = (products, storeDiscountPercentage) => {
    let discountedProducts;
    if (storeDiscountPercentage) {
        // Direct, Bulk and Store discount
        discountedProducts = (0, _discounts_1.getProductsStoreDiscount)(products, storeDiscountPercentage);
    }
    else {
        // Direct and Bulk discount
        discountedProducts = (0, _discounts_1.getProductsBulkDiscount)(products);
    }
    return discountedProducts;
};
exports.getProductListDiscountsSummary = getProductListDiscountsSummary;
// #endregion Product
// #region Wholesaler
/**
 * @deprecated use getWholesalerDiscountsSummary instead. Kept to maintain compatibility with retailer-site
 *
 * Calculates the wholesaler summary of discounts and totals
 * through the provided wholesaler products list.
 * @param wholesalerId Wholesaler ID
 * @param products List of wholesaler products
 * @param storeDiscounts List of tired store discount rule objects
 * @returns The wholesaler summary (totals and discounts)
 */
const getWholesalerSummary = (wholesalerId, products, storeDiscounts) => (0, exports.getWholesalerDiscountsSummary)(wholesalerId, products, storeDiscounts);
exports.getWholesalerSummary = getWholesalerSummary;
/**
 * Calculates the wholesaler summary of discounts and totals
 * through the provided wholesaler products list.
 * @param wholesalerId Wholesaler ID
 * @param products List of wholesaler products
 * @param storeDiscounts List of tired store discount rule objects
 * @returns The wholesaler summary (totals and discounts)
 */
const getWholesalerDiscountsSummary = (wholesalerId, products, storeDiscounts) => {
    // Get discounts and totals summary (with bulk discounts applied)
    let productsSummary = (0, exports.getProductListDiscountsSummary)(products);
    // Calculate the total store discount and check if any store discount is applicable
    const storeDiscount = storeDiscounts === null || storeDiscounts === void 0 ? void 0 : storeDiscounts.sort((discountA, discountB) => discountA.storeDiscountMinOrderAmount - discountB.storeDiscountMinOrderAmount).filter((storeDiscount) => storeDiscount.storeDiscountMinOrderAmount <= productsSummary.total).pop();
    // recalculate the products summary if a store discount is applicable
    if (storeDiscount) {
        productsSummary = (0, exports.getProductListDiscountsSummary)(products, storeDiscount.storeDiscountPercentage);
    }
    return Object.assign(Object.assign({ wholesalerId }, (storeDiscount && { storeDiscount })), { lineItemSubtotal: productsSummary.lineItemSubtotal, subtotal: productsSummary.subtotal, total: productsSummary.total, discounts: {
            direct: productsSummary.discounts.direct,
            bulk: productsSummary.discounts.bulk,
            store: productsSummary.discounts.store,
        }, products: productsSummary.products });
};
exports.getWholesalerDiscountsSummary = getWholesalerDiscountsSummary;
/**
 * Calculates the wholesaler summary of discounts and totals
 * through the provided wholesaler products list.
 * @param wholesalerId Wholesaler ID
 * @param products List of wholesaler products
 * @param storeDiscounts List of tired store discount rule objects
 * @returns The wholesaler summary (totals and discounts)
 */
const getWholesalerDiscountsSummaryWithCurrency = (wholesalerId, products, currency, storeDiscounts) => {
    // Convert the products to ProductDiscountRequest
    const productDiscountRequests = products.map((product) => {
        var _a, _b;
        const productPrice = product.productPrice.find((price) => price.productCurrency === currency);
        const productDiscountedPrice = (_b = (_a = product.productDiscount) === null || _a === void 0 ? void 0 : _a.find((discount) => discount.productDiscountCurrency === currency)) === null || _b === void 0 ? void 0 : _b.productDiscountValue;
        if (!productPrice) {
            throw new error_1.BusinessError(_shared_1.ERRORS.VALIDATION_ERROR, `Missing product price for productId ${product.productId} and currency ${currency}`);
        }
        return {
            productId: product.productId || product.productWpId,
            productPrice: productPrice === null || productPrice === void 0 ? void 0 : productPrice.productWSP,
            quantity: product.quantity,
            productDiscountedPrice,
            productBulkDiscount: product.productBulkDiscount,
        };
    });
    return (0, exports.getWholesalerDiscountsSummary)(wholesalerId, productDiscountRequests, storeDiscounts);
};
exports.getWholesalerDiscountsSummaryWithCurrency = getWholesalerDiscountsSummaryWithCurrency;
// #endregion Wholesaler
// #region Checkout
/**
 * Calculates a list of the wholesaler summary of discounts and totals
 * through the provided wholesaler products list.
 * @param wholesaler List of wholesaler products
 * @returns The wholesaler summary (totals and discounts)
 */
const getDiscountsSummary = (wholesalers, currency) => {
    const emptyCartDiscountsSummary = {
        lineItemSubtotal: 0,
        subtotal: 0,
        total: 0,
        discounts: {
            direct: 0,
            bulk: 0,
            store: 0,
        },
        wholesalers: [],
    };
    const cartSummary = wholesalers.reduce((cartDiscountsSummary, wholesaler) => {
        const discountedWholesaler = (0, exports.getWholesalerDiscountsSummaryWithCurrency)(wholesaler.wholesalerId, wholesaler.products, currency, wholesaler.storeDiscount);
        return {
            lineItemSubtotal: cartDiscountsSummary.lineItemSubtotal + discountedWholesaler.lineItemSubtotal,
            subtotal: cartDiscountsSummary.subtotal + discountedWholesaler.subtotal,
            total: cartDiscountsSummary.total + discountedWholesaler.total,
            discounts: {
                direct: cartDiscountsSummary.discounts.direct + discountedWholesaler.discounts.direct,
                bulk: cartDiscountsSummary.discounts.bulk + discountedWholesaler.discounts.bulk,
                store: cartDiscountsSummary.discounts.store + discountedWholesaler.discounts.store,
            },
            wholesalers: [...cartDiscountsSummary.wholesalers, discountedWholesaler],
        };
    }, emptyCartDiscountsSummary);
    return cartSummary;
};
exports.getDiscountsSummary = getDiscountsSummary;
// #endregion Checkout
